import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import QuestionPublicLink from "../../../component/PublicLink/PublicLink";
import QuizMarkdown from "../../../component/Markdown/QuizMarkdown";
import {
  QuizScreen,
  QuizTitle,
  QuizDescriptionDiv,
  QuizButton,
  QuizMessage,
  tint,
  darken,
} from "../../GeneralStyles";
import {
  checkAndInitializeDB,
  saveToDB,
  getFromDB,
} from "../../../component/indexedDB/indexedDB";

/**
 * Strapi component UID: quiz-game-types.photo-upload
 *
 * Gebruikte velden op `question`:
 * - quiz_item_id, title, description, theme_color, theme_text_color
 * - accept (string), camera_facing ("environment" | "user")
 * - target_width (int), target_height (int), jpeg_quality (0..1)
 * - min_width (int), min_height (int), max_filesize_kb (int)
 * - Labels: take_photo_label, upload_photo_label, take_snapshot_label,
 *           confirm_label, back_label, choose_file_label, choose_other_label,
 *           thanks_text, back_to_box_label, retake_label
 * - (optioneel) maximum_attempts (int) — zelfde semantiek als andere types
 */

const Stage = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Frame = styled.div`
  width: 100%;
  max-width: 640px;
  aspect-ratio: 4 / 3;
  background: #c9c9c9;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px #001d2910, 0 2px 4px -2px #001d2910;
`;

const VideoEl = styled.video`
  width: 100%;
  height: 100%;
  background: transparent;
  object-fit: cover;
`;

const PreviewImg = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 4px 6px -1px #001d2910, 0 2px 4px -2px #001d2910;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  max-width: 640px;
  margin-top: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const SecondaryButton = styled(QuizButton)`
  background: #ffffff !important;
  color: ${(p) => p.bgColor} !important;
  border: 2px solid ${(p) => tint(p.bgColor, 50)};
  &:hover {
    background: #fff !important;
    color: ${(p) => darken(p.bgColor, 20)} !important;
    border-color: ${(p) => darken(p.bgColor, 10)};
  }
`;

export default function QuestionUpload({ question, overViewPage, enableNavigationButton, onCancel, autoPostMessage = true }) {
  // ---------- cfg uit Strapi + defaults ----------
  const cfg = {
    accept: question?.accept || "image/*",
    cameraFacing: question?.camera_facing, // "environment" | "user" | undefined
    targetW: Number(question?.target_width) || 1280,
    targetH: Number(question?.target_height) || 1280,
    quality: question?.jpeg_quality != null ? Number(question.jpeg_quality) : 0.8,
    minW: question?.min_width != null ? Number(question.min_width) : undefined,
    minH: question?.min_height != null ? Number(question.min_height) : undefined,
    maxKB: question?.max_filesize_kb != null ? Number(question.max_filesize_kb) : 450,
  };

  const themeColor = question?.theme_color || "#3462ac";
  const themeTextColor = question?.theme_text_color || "#ffffff";

  // ---------- state & refs ----------
  const [mode, setMode] = useState("select"); // select | take | previewPhoto | upload | previewUpload | finish
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMeta, setPreviewMeta] = useState({ width: null, height: null, sizeKB: null });
  const [errorMsg, setErrorMsg] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(typeof document !== "undefined" ? document.createElement("canvas") : null);

  const computedId = question?.quiz_item_id ?? "unknown";
  const dbName = sessionStorage.getItem("currentSessionName") || "Leerling";

  // bij mount: DB init + bestaande poging inladen
  useEffect(() => {
    checkAndInitializeDB(dbName);
    if (dbName !== "Leerling") {
      getFromDB(dbName).then((data) => {
        const attemptData = data.find((item) => item.id === computedId);
        if (attemptData?.answer?.dataUrl) {
          setPreviewUrl(attemptData.answer.dataUrl);
          setPreviewMeta({
            width: attemptData.answer.width || null,
            height: attemptData.answer.height || null,
            sizeKB: attemptData.answer.sizeKB || null,
          });
          setMode("previewUpload");
          enableNavigationButton?.(); // consistent met andere types
        }
        if (attemptData) {
          setAttempts(attemptData.attempts || 0);
          if (
            question.maximum_attempts > 0 &&
            attemptData.attempts >= question.maximum_attempts
          ) {
            setIsDisabled(true);
          }
        }
      });
    }
    // cleanup stream on unmount
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedId]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // ---------- helpers ----------
  const fitBox = (srcW, srcH, maxW, maxH) => {
    if (!maxW && !maxH) return { w: srcW, h: srcH };
    const rw = maxW ? maxW / srcW : Infinity;
    const rh = maxH ? maxH / srcH : Infinity;
    const r = Math.min(rw, rh, 1); // nooit upscalen
    return { w: Math.round(srcW * r), h: Math.round(srcH * r) };
  };

  const dataURLSizeKB = (dataUrl) => {
    const base64 = (dataUrl || "").split(",")[1] || "";
    const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
    const bytes = base64.length * 0.75 - padding;
    return Math.max(0, Math.round(bytes / 1024));
  };

  const encodeCanvasWithLimits = async (
    canvas,
    { maxKB, initialQ = cfg.quality, minQ = 0.5, step = 0.05, mime = "image/jpeg" }
  ) => {
    let q = initialQ;
    let dataUrl = canvas.toDataURL(mime, q);
    let sizeKB = dataURLSizeKB(dataUrl);

    while (maxKB && sizeKB > maxKB && q > minQ) {
      q = Math.max(minQ, +(q - step).toFixed(2));
      dataUrl = canvas.toDataURL(mime, q);
      sizeKB = dataURLSizeKB(dataUrl);
      if (q <= minQ) break;
    }
    return { dataUrl, sizeKB, quality: q };
  };

  const failWith = (msg) => setErrorMsg(msg);

  const setPreview = (url, w, h, sizeKB, nextMode) => {
    setPreviewUrl(url);
    setPreviewMeta({ width: w, height: h, sizeKB });
    setMode(nextMode);
  };

  // ---------- actions ----------
  const startCamera = async () => {
    setErrorMsg("");
    if (isDisabled) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      return failWith("Camera niet beschikbaar in deze browser.");
    }
    setMode("take");
    try {
      const facing =
        cfg.cameraFacing === "environment"
          ? { ideal: "environment" }
          : cfg.cameraFacing === "user"
          ? { ideal: "user" }
          : undefined;

      const constraints = {
        video: facing ? { facingMode: facing } : true,
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      setMode("select");
      failWith("Camera niet beschikbaar: " + err.message);
    }
  };

  const shoot = async () => {
    setErrorMsg("");
    if (isDisabled) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const srcW = video.videoWidth || 640;
    const srcH = video.videoHeight || 480;

    if ((cfg.minW && srcW < cfg.minW) || (cfg.minH && srcH < cfg.minH)) {
      return failWith(
        `Foto is te klein (${srcW}×${srcH}). Minimaal vereist: ${cfg.minW || "–"}×${cfg.minH || "–"}.`
      );
    }

    const { w, h } = fitBox(srcW, srcH, cfg.targetW, cfg.targetH);
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(video, 0, 0, srcW, srcH, 0, 0, w, h);

    const { dataUrl, sizeKB } = await encodeCanvasWithLimits(canvas, {
      maxKB: cfg.maxKB,
    });

    if (cfg.maxKB && sizeKB > cfg.maxKB) {
      return failWith(
        `Bestand blijft te groot (${sizeKB} KB) ondanks compressie. Kies een andere foto of verlaag je instellingen.`
      );
    }

    setPreview(dataUrl, w, h, sizeKB, "previewPhoto");
  };

  const confirm = (source) => {
    stopStream();
    setMode("finish");

    // opslaan in IndexedDB
    const attempt = {
      id: computedId,
      answer: {
        dataUrl: previewUrl,
        width: previewMeta.width,
        height: previewMeta.height,
        sizeKB: previewMeta.sizeKB,
        source, // "camera" | "upload"
      },
      success: true,
      attempts: attempts + 1,
    };
    saveToDB([attempt], dbName);
    setAttempts((a) => a + 1);

    enableNavigationButton?.(); // nav vrijgeven

    if (autoPostMessage && typeof window !== "undefined" && window.top) {
      window.top.postMessage({ type: "endOfUploadZone", result: "succes" }, "*");
    }

    if (question.maximum_attempts > 0 && attempts + 1 >= question.maximum_attempts) {
      setIsDisabled(true);
    }
  };

  const eject = () => {
    stopStream();
    onCancel?.();
    if (autoPostMessage && typeof window !== "undefined" && window.top) {
      window.top.postMessage({ type: "endOfUploadZone", result: "fail" }, "*");
    }
    // geen enableNavigationButton() bij annuleren
  };

  const onFileButtonClick = (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (isDisabled) return;
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    setErrorMsg("");
    const file = e.target.files?.[0];
    if (!file) return;

    // zelfde bestand opnieuw kunnen kiezen
    e.target.value = "";

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const url = ev.target.result;
      const img = new Image();
      img.onload = async () => {
        const srcW = img.naturalWidth;
        const srcH = img.naturalHeight;

        if ((cfg.minW && srcW < cfg.minW) || (cfg.minH && srcH < cfg.minH)) {
          return failWith(
            `Afbeelding is te klein (${srcW}×${srcH}). Minimaal vereist: ${cfg.minW || "–"}×${cfg.minH || "–"}.`
          );
        }

        const { w, h } = fitBox(srcW, srcH, cfg.targetW, cfg.targetH);
        const canvas = canvasRef.current;
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, srcW, srcH, 0, 0, w, h);

        const { dataUrl, sizeKB } = await encodeCanvasWithLimits(canvas, {
          maxKB: cfg.maxKB,
        });

        if (cfg.maxKB && sizeKB > cfg.maxKB) {
          return failWith(
            `Bestand blijft te groot (${sizeKB} KB) ondanks compressie. Kies een andere foto of verlaag je instellingen.`
          );
        }

        setPreview(dataUrl, w, h, sizeKB, "previewUpload");
      };
      img.onerror = () => failWith("Kon afbeelding niet laden.");
      img.src = url;
    };
    reader.onerror = () => failWith("Kon bestand niet lezen.");
    reader.readAsDataURL(file);
  };

  // ---------- UI ----------
  const computedAccept = cfg.accept;
  const captureAttr =
    cfg.cameraFacing === "environment"
      ? "environment"
      : cfg.cameraFacing === "user"
      ? "user"
      : undefined;

  return (
    <QuizScreen bgColor={question.theme_color} bgVisible={overViewPage} id={question.quiz_item_id}>
      <QuestionPublicLink
        overViewPage={overViewPage}
        type="question"
        quiz_item_id={question.quiz_item_id}
      />

      <QuizTitle>{question.title}</QuizTitle>
      {question?.description && (
        <QuizDescriptionDiv>
          <QuizMarkdown>{question.description}</QuizMarkdown>
        </QuizDescriptionDiv>
      )}

      <Stage>
        

        {errorMsg && (
          <QuizMessage color="#a40000" style={{ background: "#ffecec", borderRadius: 6, padding: "8px 12px" }}>
            {errorMsg}
          </QuizMessage>
        )}

        {/* Select */}
        {mode === "select" && (
          <ButtonRow>
            <QuizButton
              onClick={startCamera}
              bgColor={themeColor}
              txtColor={themeTextColor}
              disabled={isDisabled}
            >
              {question?.take_photo_label || "Maak een foto"}
            </QuizButton>
            <QuizButton
              onClick={() => setMode("upload")}
              bgColor={themeColor}
              txtColor={themeTextColor}
              disabled={isDisabled}
            >
              {question?.upload_photo_label || "Foto uploaden"}
            </QuizButton>
          </ButtonRow>
        )}

        {/* Take */}
        {mode === "take" && (
          <>
            <Frame>
              <VideoEl ref={videoRef} autoPlay muted playsInline />
            </Frame>
            <ButtonRow>
              <SecondaryButton
                onClick={() => {
                  stopStream();
                  setMode("select");
                }}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.back_label || "Terug"}
              </SecondaryButton>
              <QuizButton
                onClick={shoot}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.take_snapshot_label || "Neem een foto"}
              </QuizButton>
            </ButtonRow>
          </>
        )}

        {/* Preview camera */}
        {mode === "previewPhoto" && (
          <>
            {previewUrl && (
              <PreviewImg src={previewUrl} alt="Voorbeeld van genomen foto" />
            )}
            <ButtonRow>
              <SecondaryButton
                onClick={() => {
                  setPreviewUrl(null);
                  startCamera();
                }}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.retake_label || "Maak een andere foto"}
              </SecondaryButton>
              <QuizButton
                onClick={() => confirm("camera")}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.confirm_label || "Ik ben tevreden"}
              </QuizButton>
            </ButtonRow>
          </>
        )}

        {/* Upload */}
        {mode === "upload" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={computedAccept}
              capture={captureAttr}
              onChange={onFileChange}
              style={{ display: "none" }}
            />
            <ButtonRow>
              <SecondaryButton
                onClick={() => setMode("select")}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.back_label || "Terug"}
              </SecondaryButton>
              <QuizButton
                onClick={onFileButtonClick}
                bgColor={themeColor}
                txtColor={themeTextColor}
                disabled={isDisabled}
              >
                {question?.choose_file_label || "Kies een foto"}
              </QuizButton>
            </ButtonRow>
          </>
        )}

        {/* Preview upload */}
        {mode === "previewUpload" && (
          <>
            {previewUrl && (
              <PreviewImg
                src={previewUrl}
                alt="Snelle weergave van geüploade afbeelding"
              />
            )}
            <ButtonRow>
              <SecondaryButton
                onClick={() => {
                  setPreviewUrl(null);
                  setMode("upload");
                }}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.choose_other_label || "Kies andere foto"}
              </SecondaryButton>
              <QuizButton
                onClick={() => confirm("upload")}
                bgColor={themeColor}
                txtColor={themeTextColor}
              >
                {question?.confirm_label || "Ik ben tevreden"}
              </QuizButton>
            </ButtonRow>
          </>
        )}

        {/* Finish */}
        {mode === "finish" && (
          <>
            <QuizMessage style={{ whiteSpace: "pre-line" }}>
              {question?.thanks_text ||
                "Bedankt om een foto in te sturen.\nDe foto is bewaard."}
            </QuizMessage>
            <QuizButton onClick={() => {}} bgColor={themeColor} txtColor={themeTextColor}>
              {question?.back_to_box_label || "Terug naar de EDUbox"}
            </QuizButton>
          </>
        )}
      </Stage>
    </QuizScreen>
  );
}
